import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Label } from "@/components/ui/label";

// Self-contained horizontal topic selector. Built from plain <button>
// elements (not Radix TabsList) so it isn't sensitive to stacking
// contexts or focus-management side effects. The parent still wraps
// the question content in <Tabs value=… onValueChange=…> so that
// <TabsContent value=…> rendering keeps working.
//
// Features (visually identical to the prior implementation):
//   - Active tab in orange-400 / white text.
//   - Tabs grow to share leftover row space; shrink-0 so
//     long titles overflow horizontally instead of wrapping or
//     squishing.
//   - Left / right arrow buttons step the active topic by ±1 index
//     (purely index-based — no visual scroll math) and scroll the
//     new active tab into view.
//   - Click-and-drag scrolling on the row. Document-level mousemove/
//     mouseup so the grip survives the cursor leaving the row.
//   - Native scrollbar hidden; replaced by a thin non-interactive
//     progress indicator below the row.

interface Topic {
  id: string;
  name: string;
}

interface TopicSelectorProps {
  topics: Topic[];
  value: string | undefined;
  onChange: (id: string) => void;
  /** Label shown above the row. */
  label?: string;
}

const ARROW_PAD = 48; // px of space the arrow overlay covers — keep snap target past it

export function TopicSelector({ topics, value, onChange, label = "Test topics" }: TopicSelectorProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState({ scrollLeft: 0, scrollWidth: 0, clientWidth: 0 });
  const dragRef = useRef({ isDragging: false, startX: 0, startScrollLeft: 0, hasDragged: false });

  const updateMetrics = () => {
    const el = scrollRef.current;
    if (!el) return;
    setMetrics({
      scrollLeft: el.scrollLeft,
      scrollWidth: el.scrollWidth,
      clientWidth: el.clientWidth,
    });
  };
  useEffect(() => {
    updateMetrics();
    window.addEventListener("resize", updateMetrics);
    return () => window.removeEventListener("resize", updateMetrics);
  }, [topics.length]);

  // When the active value changes (whether from a tab click, an arrow,
  // or a parent-side change) bring the corresponding tab into view if
  // it isn't already fully visible.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !value) return;
    const idx = topics.findIndex((t) => t.id === value);
    if (idx < 0) return;
    const target = el.children[idx] as HTMLElement | undefined;
    if (!target) return;
    const targetLeft = target.offsetLeft;
    const targetRight = targetLeft + target.offsetWidth;
    const viewLeft = el.scrollLeft;
    const viewRight = viewLeft + el.clientWidth;
    if (targetLeft < viewLeft + ARROW_PAD) {
      el.scrollTo({ left: Math.max(0, targetLeft - ARROW_PAD), behavior: "smooth" });
    } else if (targetRight > viewRight - ARROW_PAD) {
      el.scrollTo({
        left: targetRight - el.clientWidth + ARROW_PAD,
        behavior: "smooth",
      });
    }
  }, [value, topics]);

  const currentIdx = topics.findIndex((t) => t.id === value);
  const canGoPrev = currentIdx > 0;
  const canGoNext = currentIdx >= 0 && currentIdx < topics.length - 1;
  const hasOverflow = metrics.scrollWidth > metrics.clientWidth + 1;

  const handlePrev = () => {
    if (!canGoPrev) return;
    onChange(topics[currentIdx - 1].id);
  };
  const handleNext = () => {
    if (!canGoNext) return;
    onChange(topics[currentIdx + 1].id);
  };

  // Document-level drag so the grip persists outside the row. Drag is
  // suppressed on the arrow buttons via onMouseDown stopPropagation.
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current = {
      isDragging: true,
      startX: e.pageX,
      startScrollLeft: el.scrollLeft,
      hasDragged: false,
    };
    const onMove = (moveEvent: MouseEvent) => {
      if (!dragRef.current.isDragging) return;
      const node = scrollRef.current;
      if (!node) return;
      const dx = moveEvent.pageX - dragRef.current.startX;
      if (Math.abs(dx) > 4) dragRef.current.hasDragged = true;
      if (dragRef.current.hasDragged) {
        moveEvent.preventDefault();
        node.scrollLeft = dragRef.current.startScrollLeft - dx;
      }
    };
    const onUp = () => {
      dragRef.current.isDragging = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // If the user dragged the row, swallow the click on the released tab
  // so dragging-to-scroll doesn't accidentally activate a topic.
  const handleTabClick = (topicId: string) => (e: React.MouseEvent) => {
    if (dragRef.current.hasDragged) {
      e.preventDefault();
      dragRef.current.hasDragged = false;
      return;
    }
    onChange(topicId);
  };

  return (
    <div className="mb-8 mt-6">
      <Label className="text-lg font-semibold block mb-2">{label}</Label>
      <div className="relative">
        <div
          ref={scrollRef}
          onScroll={updateMetrics}
          onMouseDown={handleMouseDown}
          className="flex w-full overflow-x-auto gap-2 cursor-grab active:cursor-grabbing select-none [&::-webkit-scrollbar]:hidden scrollbar-none [-ms-overflow-style:none]"
        >
          {topics.map((topic) => {
            const isActive = topic.id === value;
            return (
              <button
                key={topic.id}
                type="button"
                onClick={handleTabClick(topic.id)}
                className={`grow shrink-0 whitespace-nowrap text-base px-4 py-2 rounded-xs border transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground border-primary shadow-xs"
                    : "bg-background text-foreground border-border hover:bg-accent"
                }`}
                data-testid={`topic-${topic.id}`}
              >
                {topic.name}
              </button>
            );
          })}
        </div>
        {/* Arrow overlays are split into two layers so that the
            gradient (visual fade) doesn't steal clicks from chips
            underneath. The faded background sits on z-10 with
            pointer-events-none — chips beneath stay clickable. The
            small chevron-only button sits on z-20 with the actual
            click handler. */}
        {canGoPrev && (
          <>
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-10 bg-linear-to-r from-background via-background/90 to-transparent" />
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handlePrev}
              aria-label="Previous topic"
              className="absolute left-1 top-1/2 -translate-y-1/2 z-20 p-1 rounded-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          </>
        )}
        {canGoNext && (
          <>
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 z-10 bg-linear-to-l from-background via-background/90 to-transparent" />
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleNext}
              aria-label="Next topic"
              className="absolute right-1 top-1/2 -translate-y-1/2 z-20 p-1 rounded-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>
      {hasOverflow && (
        <div className="relative mt-1 h-0.5 w-full bg-muted/40 rounded-full">
          <div
            className="absolute top-0 bottom-0 bg-muted-foreground/60 rounded-full"
            style={{
              left: `${(metrics.scrollLeft / metrics.scrollWidth) * 100}%`,
              width: `${(metrics.clientWidth / metrics.scrollWidth) * 100}%`,
            }}
          />
        </div>
      )}
    </div>
  );
}
